
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { cleanupAuthState } from "@/utils/authUtils";

export const GroupLeaderRegistration = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Check if user is already authenticated on mount
  useEffect(() => {
    const checkExistingSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          // If already logged in, redirect to dashboard
          navigate("/admin/dashboard");
        }
      } catch (error) {
        console.error("Error checking session:", error);
        cleanupAuthState(); // Clean up if there's an error
      }
    };
    
    checkExistingSession();
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);

      // Get the current session
      const { data: { session } } = await supabase.auth.getSession();

      // Create the registration request
      const { error: requestError } = await supabase
        .from("group_leader_requests")
        .insert({ email, full_name: fullName });

      if (requestError) {
        console.error("Request creation error:", requestError);
        if (requestError.code === '23505') {
          throw new Error("En registreringsförfrågan med denna e-postadress finns redan.");
        }
        throw new Error("Kunde inte skapa registreringsförfrågan");
      }

      // Send email notification with the access token if available
      const { error: emailError } = await supabase.functions.invoke('send-approval-email', {
        body: { email, full_name: fullName },
        headers: session?.access_token ? {
          Authorization: `Bearer ${session.access_token}`,
        } : undefined
      });

      if (emailError) {
        console.error("Email notification error:", emailError);
        // Don't throw here, as the request was created successfully
      }

      toast({
        title: "Förfrågan skickad",
        description: "Din förfrågan har skickats och väntar på godkännande.",
      });

      navigate("/admin");
    } catch (error: any) {
      console.error("Registration error:", error);
      toast({
        title: "Ett fel uppstod",
        description: error.message || "Kunde inte skicka förfrågan. Försök igen.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container max-w-md py-8">
      <Card className="p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <h1 className="text-2xl font-semibold tracking-tight">
              Registrera som gruppledare
            </h1>
            <p className="text-sm text-muted-foreground">
              Fyll i dina uppgifter för att ansöka om att bli gruppledare.
            </p>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">E-post</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="fullName">Fullständigt namn</Label>
              <Input
                id="fullName"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
              />
            </div>
          </div>

          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? "Skickar..." : "Skicka förfrågan"}
          </Button>
        </form>
      </Card>
    </div>
  );
};

export default GroupLeaderRegistration;
